import { useState } from 'react';
import { StatusBar, Modal, SnackbarStatus } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import styles from './GlobalItemDetailsEditModal.module.scss';
import { FieldNamesMarkedBoolean } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../hooks/QueryKeys';
import { updateGlobalItemDetails } from 'src/api/updateGlobalItemDetails';
import { formUpdateDetailsPayload } from './formUpdateDetailsPayload/formUpdateDetailsPayload';
import { GlobalItemDetailsForm } from 'src/components/CreateGlobalItemWizard/GlobalItemDetailsForm/GlobalItemDetailsForm';
import { IGlobalItem } from '../CreateGlobalItemWizard/types';
import chefPlating from 'src/assets/images/chef-plating.png';
import { FormVariant } from '../../types/menuItemForm.types';
import { hasRegulatoryStatement } from 'src/utils/regulatory-statements';
import { REGULATORY_STATEMENTS } from '../../constants';
import { IMenuItem, IRecipe } from 'src/types/menu.types';

export interface IGlobalItemDetails {
  currentItem: IMenuItem;
  selectedRecipe: IRecipe;
}

interface IGlobalItemDetailsEditModal extends Omit<IGlobalItemDetails, 'selectedRecipe'> {
  onModalClose: () => void;
  isModalOpen: boolean;
  showImageField?: boolean;
}

const GlobalItemDetailsEditForm = ({
  onModalClose,
  currentItem,
  showImageField = false,
}: Omit<IGlobalItemDetailsEditModal, 'isModalOpen'>) => {
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const onEdit = async (
    data: Partial<IGlobalItem>,
    dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<IGlobalItem>>>,
    isDirty: boolean,
  ) => {
    clearError();
    try {
      if (isDirty) {
        setIsSubmitting(true);

        const payload = formUpdateDetailsPayload(data, dirtyFields);

        await updateGlobalItemDetails(currentItem.itemId, payload);

        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.item, currentItem.itemId],
        });

        showNotification({
          status: SnackbarStatus.SUCCESS,
          message: `You have successfully updated "${currentItem.name}".`,
        });
      }
      onModalClose();
    } catch {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: 'Something went wrong whilst updating a product',
      });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <GlobalItemDetailsForm
        variant={FormVariant.UPDATE}
        item={{
          name: currentItem.name,
          consumer_name: currentItem.consumerName,
          recipe_id: currentItem.recipe?.product_id,
          description: currentItem.description,
          plu: currentItem.plu,
          image: currentItem.image,
          type: currentItem.type,
          containsAlcohol: currentItem.containsAlcohol,
          is_hfss: hasRegulatoryStatement(currentItem.regulatory_statements, REGULATORY_STATEMENTS.HFSS.name),
          age_verification: hasRegulatoryStatement(
            currentItem.regulatory_statements,
            REGULATORY_STATEMENTS.AGE_VERIFICATION.name,
          ),
        }}
        isSubmitting={isSubmitting}
        onSaveItem={onEdit}
        onCloseOrBack={onModalClose}
        showImageField={showImageField}
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </>
  );
};

const GlobalItemDetailsEditModal = ({
  isModalOpen,
  onModalClose,
  currentItem,
  showImageField = false,
}: IGlobalItemDetailsEditModal) => (
  <Modal open={isModalOpen} closeModal={onModalClose} image={chefPlating} imageAlt="chef">
    <GlobalItemDetailsEditForm onModalClose={onModalClose} currentItem={currentItem} showImageField={showImageField} />
  </Modal>
);
export default GlobalItemDetailsEditModal;
